import { DISPATCH_WINDOW_SEC } from "../audio/constants";
import { MAX_TIMING_OFFSET_MS } from "../music/PerformanceEngine";
import type { PendingQueue } from "./PendingQueue";
import type { NoteEvent, RampEvent, ScheduledEvent } from "./ScheduledEvent";

/**
 * Where reserved events are sent. Kept as an interface so the dispatcher can be
 * unit tested without Web Audio.
 */
export interface DispatchSink {
  note(event: NoteEvent, timeSec: number): void;
  ramp(event: RampEvent, timeSec: number): void;
}

/**
 * The single time base for reservations.
 *
 * `callbackTimeSec` is the `time` argument Tone passes to the scheduleRepeat
 * callback. It is NOT the same as Tone.now() / AudioContext.currentTime: the
 * callback fires roughly `lookAhead` seconds early. Mixing the two introduces a
 * systematic error and inflates lateDropCount, so this is the only accepted
 * origin. See docs/implementation-plan.md 9.13.1.
 */
export interface DispatchClock {
  callbackTimeSec: number;
  callbackTicks: number;
  secondsPerTick: number;
}

export interface DispatchResult {
  dispatched: number;
  lateDropped: number;
}

/**
 * How far before the callback origin an event may still be reserved.
 *
 * Humanisation deliberately pulls some roles ahead of the grid (hats sit a few
 * ms early), so a slightly negative offset is a feature, not lateness. The
 * callback itself arrives `lookAhead` ahead of the audio clock, so an event up
 * to the humanisation budget early is still comfortably in the future.
 *
 * Only events beyond this tolerance are genuinely late and get dropped.
 */
const EARLY_TOLERANCE_SEC = MAX_TIMING_OFFSET_MS / 1000;

export class RollingDispatcher {
  constructor(
    private readonly queue: PendingQueue,
    private readonly sink: DispatchSink,
    private readonly windowSec: number = DISPATCH_WINDOW_SEC,
  ) {}

  dispatch(clock: DispatchClock): DispatchResult {
    const { callbackTimeSec, callbackTicks, secondsPerTick } = clock;
    if (secondsPerTick <= 0) return { dispatched: 0, lateDropped: 0 };

    const windowTicks = this.windowSec / secondsPerTick;
    const due = this.queue.takeDue(callbackTicks, windowTicks);

    let dispatched = 0;
    let lateDropped = 0;

    for (const event of due) {
      const timeSec = this.eventTimeSec(event, callbackTimeSec, callbackTicks, secondsPerTick);
      if (timeSec < callbackTimeSec - EARLY_TOLERANCE_SEC) {
        // Genuinely in the past, beyond what humanisation could explain:
        // reserving it would fire immediately and out of time. Drop and count.
        lateDropped += 1;
        continue;
      }
      if (event.kind === "note") {
        this.sink.note(event, timeSec);
      } else {
        this.sink.ramp(event, timeSec);
      }
      dispatched += 1;
    }

    this.queue.prune(callbackTicks);
    return { dispatched, lateDropped };
  }

  private eventTimeSec(
    event: ScheduledEvent,
    callbackTimeSec: number,
    callbackTicks: number,
    secondsPerTick: number,
  ): number {
    const base = callbackTimeSec + (event.timeTicks - callbackTicks) * secondsPerTick;
    return event.kind === "note" ? base + event.timingOffsetMs / 1000 : base;
  }
}

/** seconds per transport tick at a given tempo. */
export function secondsPerTick(bpm: number, ppq: number): number {
  if (bpm <= 0 || ppq <= 0) return 0;
  return 60 / (bpm * ppq);
}
